import { RefObject, useContext, useEffect, useMemo, useState } from 'react';

import breakpoints from '@/mui/constants/breakpoints';

import { BreakpointsContext } from '@/modules/common/components/BreakpointsProvider/helpers';

import { GlobalIntersectionObserver } from './observers';

/**
 * Hook that checks if a given element is on screen using an Intersection Observer.
 * @param ref - A reference to the element to observe.
 * @param onlyOnce - Whether to stop observing after the first intersection.
 * @returns `true` if the element is intersecting, otherwise `false`.
 */
export const useOnScreen = (ref: RefObject<Element | null>, onlyOnce = false): boolean => {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const el = ref.current;

    const fn = (entry: IntersectionObserverEntry) => {
      if (onlyOnce) {
        if (entry.isIntersecting) {
          setIntersecting(true);

          if (el) {
            GlobalIntersectionObserver.unobserve(el);
          }
        }
      } else {
        setIntersecting(entry.isIntersecting);
      }
    };

    if (el) {
      GlobalIntersectionObserver.observe(el, fn);
    }

    return () => {
      if (el) {
        GlobalIntersectionObserver.unobserve(el);
      }
    };
  }, [ref.current, onlyOnce]);

  return isIntersecting;
};

/**
 * Hook that determines if the screen is considered "small" based on width and context.
 * @returns `true` if the screen width is small, otherwise `false`.
 */
const keys: Record<number, string> = Object.entries(breakpoints.values).reduce(
  (acc, [key, value]) => {
    acc[value] = key;
    return acc;
  },
  {} as Record<number, string>,
);

export const useBreakpointRange = (from: number, to?: number): boolean => {
  const breakpointList = useContext(BreakpointsContext);

  return useMemo(() => breakpointList.includes(`${keys[from]}-${to ? keys[to] : 'infinity'}`), [breakpointList]);
};

export const useSmallScreen = (): boolean => useBreakpointRange(breakpoints.values.xs, breakpoints.values.x2l);
